import { createTheme } from '@mui/material/styles';
import { california, kepple, neonBlue, redOrange, shakespeare, stormGrey } from './colors';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      light: neonBlue[400],
      main: neonBlue[500],
      dark: neonBlue[600],
      contrastText: '#ffffff',
    },
    secondary: {
      main: stormGrey[700],
      light: stormGrey[500],
      dark: stormGrey[800],
      contrastText: '#ffffff',
    },
    success: {
      main: kepple[500],
      dark: kepple[600],
    },
    warning: {
      main: california[500],
    },
    error: {
      main: redOrange[500],
    },
    info: {
      main: shakespeare[500],
    },
    background: {
      default: stormGrey[50],
      paper: '#ffffff',
    },
    text: {
      primary: stormGrey[900],
      secondary: stormGrey[500],
    },
    divider: stormGrey[200],
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
    h1: { fontSize: '3.5rem', fontWeight: 500, lineHeight: 1.2 },
    h2: { fontSize: '3rem', fontWeight: 500, lineHeight: 1.2 },
    h3: { fontSize: '2.25rem', fontWeight: 500, lineHeight: 1.2 },
    h4: { fontSize: '1.5rem', fontWeight: 500, lineHeight: 1.25 },
    h5: { fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.3 },
    h6: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.35 },
    subtitle1: { fontSize: '0.9375rem', fontWeight: 500, lineHeight: 1.45 },
    subtitle2: { fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.45 },
    body1: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.45 },
    body2: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.45 },
    button: { fontWeight: 500, textTransform: 'none' },
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(33, 38, 54, 0.08)',
    '0px 1px 5px rgba(33, 38, 54, 0.08)',
    '0px 1px 8px rgba(33, 38, 54, 0.08)',
    '0px 1px 10px rgba(33, 38, 54, 0.08)',
    '0px 1px 14px rgba(33, 38, 54, 0.08)',
    '0px 1px 18px rgba(33, 38, 54, 0.08)',
    '0px 2px 16px rgba(33, 38, 54, 0.08)',
    '0px 3px 14px rgba(33, 38, 54, 0.08)',
    '0px 3px 16px rgba(33, 38, 54, 0.08)',
    '0px 4px 18px rgba(33, 38, 54, 0.08)',
    '0px 4px 20px rgba(33, 38, 54, 0.08)',
    '0px 5px 22px rgba(33, 38, 54, 0.08)',
    '0px 6px 24px rgba(33, 38, 54, 0.08)',
    '0px 7px 26px rgba(33, 38, 54, 0.08)',
    '0px 8px 28px rgba(33, 38, 54, 0.08)',
    '0px 9px 30px rgba(33, 38, 54, 0.08)',
    '0px 10px 32px rgba(33, 38, 54, 0.08)',
    '0px 11px 34px rgba(33, 38, 54, 0.08)',
    '0px 12px 36px rgba(33, 38, 54, 0.08)',
    '0px 13px 38px rgba(33, 38, 54, 0.08)',
    '0px 14px 40px rgba(33, 38, 54, 0.08)',
    '0px 15px 42px rgba(33, 38, 54, 0.08)',
    '0px 16px 44px rgba(33, 38, 54, 0.08)',
    '0px 17px 46px rgba(33, 38, 54, 0.08)',
    '0px 18px 48px rgba(33, 38, 54, 0.08)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: '"Inter", sans-serif',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        size: 'small',
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          minHeight: 34,
          padding: '6px 12px',
          fontSize: '0.8125rem',
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: neonBlue[600],
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          boxShadow: '0px 1px 5px rgba(33, 38, 54, 0.08)',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 12,
          '&:last-child': {
            paddingBottom: 12,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: 12,
        },
      },
    },
    MuiTable: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '8px 12px',
          fontSize: '0.8125rem',
          lineHeight: 1.4,
        },
        head: {
          fontWeight: 600,
          color: stormGrey[700],
          backgroundColor: stormGrey[50],
          padding: '8px 12px',
          whiteSpace: 'nowrap',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
        margin: 'dense',
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
        margin: 'dense',
      },
    },
    MuiInputLabel: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiPagination: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          padding: '4px 12px',
          alignItems: 'center',
        },
        message: {
          padding: '4px 0',
          fontSize: '0.8125rem',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          height: 24,
          fontSize: '0.75rem',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          overflow: 'hidden',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.125rem',
          fontWeight: 700,
          padding: '18px 24px',
          flexShrink: 0,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '20px 24px',
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
        },
        dividers: {
          borderColor: stormGrey[200],
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          flexShrink: 0,
          backgroundColor: stormGrey[50],
          borderTop: `1px solid ${stormGrey[200]}`,
        },
      },
    },
  },
});

export default theme;
